<template>
    <v-menu
        :key="menuKey"
        :model-value="open"
        location="bottom start"
        offset="6"
        :close-on-content-click="false"
        :scrim="false"
        @update:model-value="$emit('update:open', $event)"
    >
        <template #activator="{ props: menuProps }">
            <span v-if="variant === 'ware-button'" class="d-inline-flex">
                <v-btn
                    v-bind="menuProps"
                    size="default"
                    color="yellow"
                    variant="outlined"
                    @click.stop
                >
                    {{ $t("entries.distanceCalculator") }}
                </v-btn>
                <v-tooltip activator="parent" location="bottom">
                    {{ $t("entries.distanceCalculatorTooltip") }}
                </v-tooltip>
            </span>

            <span v-else-if="variant === 'toolbar-button'" class="d-inline-flex">
                <v-btn
                    v-bind="menuProps"
                    density="compact"
                    color="primary"
                    variant="flat"
                    data-cy="publish-calculator-menu-button"
                    @click.stop
                >
                    {{ $t("entries.distanceCalculator") }}
                </v-btn>
                <v-tooltip activator="parent" location="bottom">
                    {{ $t("entries.distanceCalculatorTooltip") }}
                </v-tooltip>
            </span>

            <span
                v-else
                v-bind="menuProps"
                class="toolbar-left-group cursor-pointer"
                @click.stop
                @mousedown.stop
                @touchstart.stop
            >
                <span class="text-white">{{ $t("entries.tripDuration") }}:&nbsp;</span>
                <span class="text-yellow">{{ tripDurationLabel }}</span>
                <v-tooltip activator="parent" location="bottom">
                    {{ $t("entries.tripDurationTooltip") }}
                </v-tooltip>
            </span>
        </template>

        <TripDurationPanel
            :entry="entry"
            :show-distance-input="showDistanceInput"
            :distance-km="distanceKm"
            :speed-kmh="speedKmh"
            :speed-readonly="speedReadonly"
            @update:distance-km="$emit('update:distanceKm', $event)"
            @update:speed-kmh="$emit('update:speedKmh', $event)"
        />
    </v-menu>
</template>

<script>
import { formatDurationCompact } from "@/utils";
import { computeEtaBreakdown } from "@/utils/etaCalculation";
import TripDurationPanel from "./TripDurationPanel.vue";

export default {
    name: "TripDurationMenu",
    components: {
        TripDurationPanel,
    },
    props: {
        entry: {
            type: Object,
            default: null,
        },
        open: {
            type: Boolean,
            default: false,
        },
        distanceKm: {
            type: [String, Number],
            default: "",
        },
        showDistanceInput: {
            type: Boolean,
            default: false,
        },
        speedKmh: {
            type: [String, Number],
            default: "",
        },
        speedReadonly: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: "ware-button",
            validator: (value) =>
                ["ware-button", "toolbar-button", "truck-label"].includes(value),
        },
    },
    emits: ["update:open", "update:speedKmh", "update:distanceKm"],
    computed: {
        menuKey() {
            const entryUid = this.entry?.uid ?? "publish";
            return `trip-duration-${entryUid}-${this.variant}`;
        },
        effectiveDistanceKm() {
            if (this.showDistanceInput) {
                return this.distanceKm;
            }
            return this.entry?.distance_km;
        },
        tripDurationLabel() {
            const speed = Number(this.speedKmh);
            if (!Number.isFinite(speed) || speed <= 0) return null;
            const breakdown = computeEtaBreakdown({
                distanceKm: this.effectiveDistanceKm,
                avgSpeedKmh: speed,
            });
            if (!breakdown) return null;
            return formatDurationCompact(breakdown.totalTripSeconds);
        },
    },
};
</script>
